import { companyDay } from "./time";
// Mirrors backend/tasks/schemas.py TASK_TYPES exactly.
export const TASK_TYPES = [
  { id: "call", label: "Appel" },
  { id: "rdv", label: "RDV" },
  { id: "demo", label: "Démo" },
  { id: "suivi_commercial", label: "Suivi commercial" },
  { id: "suivi_pilote", label: "Suivi pilote" },
  { id: "relance", label: "Relance" },
  { id: "migration", label: "Migration" },
  { id: "onboarding", label: "Onboarding" },
  { id: "autre", label: "Autre" },
];

export const TASK_TYPE_LABELS = Object.fromEntries(TASK_TYPES.map((t) => [t.id, t.label]));

const TASK_TYPE_BADGE_COLORS = {
  call: "bg-blue-50 text-blue-700",
  rdv: "bg-violet-50 text-violet-700",
  demo: "bg-purple-50 text-purple-700",
  suivi_commercial: "bg-teal-50 text-teal-700",
  suivi_pilote: "bg-amber-50 text-amber-700",
  relance: "bg-rose-50 text-rose-700",
  migration: "bg-cyan-50 text-cyan-700",
  onboarding: "bg-emerald-50 text-emerald-700",
  autre: "bg-slate-100 text-slate-600",
};

export function taskTypeBadgeClass(type) {
  return TASK_TYPE_BADGE_COLORS[type] || TASK_TYPE_BADGE_COLORS.autre;
}

// Mirrors backend/tasks/schemas.py TASK_STATUSES. "en_retard" is never
// stored: it's a derived view (!done && due_date < now) layered on top of
// a_faire/en_cours. "annulee" is a stored state, closed exactly like "termine"
// for every done-derived consumer (overdue, reminders, dashboard count), and
// reached only via the edit drawer's Statut select (never the one-click
// cycle, since cancelling requires a reason).
export const TASK_STATUSES = [
  { id: "a_faire", label: "À faire" },
  { id: "en_cours", label: "En cours" },
  { id: "termine", label: "Terminée" },
  { id: "annulee", label: "Annulée" },
];

export const TASK_STATUS_LABELS = Object.fromEntries(TASK_STATUSES.map((s) => [s.id, s.label]));

const TASK_STATUS_BADGE_COLORS = {
  a_faire: "bg-slate-100 text-slate-600",
  en_cours: "bg-blue-50 text-blue-700",
  termine: "bg-emerald-50 text-emerald-700",
  en_retard: "bg-red-50 text-red-700",
  annulee: "bg-slate-200 text-slate-500",
};

export function taskStatusBadgeClass(status) {
  return TASK_STATUS_BADGE_COLORS[status] || TASK_STATUS_BADGE_COLORS.a_faire;
}

// Same semantic colors as taskStatusBadgeClass, as a solid dot instead of a
// pale badge background (the Timeline and mobile List cards show a status dot
// next to the title rather than a full badge).
const TASK_STATUS_DOT_COLORS = {
  a_faire: "bg-slate-400",
  en_cours: "bg-blue-500",
  termine: "bg-emerald-500",
  en_retard: "bg-red-500",
  annulee: "bg-slate-400",
};

export function taskStatusDotClass(status) {
  return TASK_STATUS_DOT_COLORS[status] || TASK_STATUS_DOT_COLORS.a_faire;
}

// due date in the past on a task that isn't done yet; never persisted.
// Never takes a second argument from the caller: it is used as `.filter(isTaskOverdue)`, where the array index would land there.
export function isTaskOverdue(task) {
  return !task.done && !!task.due_date && String(task.due_date).slice(0, 10) <= companyDay();
}

export function taskDisplayStatus(task) {
  if (isTaskOverdue(task)) return "en_retard";
  return task.status || (task.done ? "termine" : "a_faire");
}

// Status tab/filter predicate. "en_retard" is a cross-cutting view (open +
// past due), not a stored status: an overdue en_cours/a_faire task still
// belongs to its own tab as well as to "En retard".
export function taskMatchesStatusFilter(task, statusFilter) {
  if (statusFilter === "all") return true;
  if (statusFilter === "en_retard") return isTaskOverdue(task);
  return (task.status || (task.done ? "termine" : "a_faire")) === statusFilter;
}

// Mirrors backend/tasks/schemas.py PRIORITY_LEVELS.
export const PRIORITY_LEVELS = [
  { id: "low", label: "Basse" },
  { id: "medium", label: "Moyenne" },
  { id: "high", label: "Haute" },
  { id: "critical", label: "Critique" },
];

export const PRIORITY_LABELS = Object.fromEntries(PRIORITY_LEVELS.map((p) => [p.id, p.label]));

const PRIORITY_BADGE_COLORS = {
  low: "bg-slate-100 text-slate-600",
  medium: "bg-amber-50 text-amber-700",
  high: "bg-red-50 text-red-700",
  // Solid, not pale: the one priority that should read as unmistakably more
  // severe than "high" at a glance, same escalation idiom as a destructive button.
  critical: "bg-red-600 text-white",
};

export function priorityBadgeClass(priority) {
  return PRIORITY_BADGE_COLORS[priority] || PRIORITY_BADGE_COLORS.medium;
}

// Mirrors backend/tasks/schemas.py TASK_DOMAINS: a closed business-domain
// taxonomy, distinct from TASK_TYPES (the kind of action). Mandatory on every
// new task; older tasks read back with domain: null (backend/tasks/service.py
// normalize_task), rendered as "—" rather than defaulting to one of these.
export const TASK_DOMAINS = [
  { id: "pilots", label: "Pharmacies pilotes" },
  { id: "product", label: "Produit" },
  { id: "onboarding", label: "Onboarding" },
  { id: "commercial", label: "Commercial" },
  { id: "support", label: "Support" },
  { id: "legal_admin", label: "Juridique / Admin" },
  { id: "finance", label: "Finance" },
  { id: "infrastructure", label: "Infrastructure" },
  { id: "internal_organization", label: "Organisation interne" },
  { id: "customer_success", label: "Suivi client" },
];

export const TASK_DOMAIN_LABELS = Object.fromEntries(TASK_DOMAINS.map((d) => [d.id, d.label]));

// One neutral badge color for every domain: a classification axis, not a
// severity scale like priority. Same indigo family lib/activity.js uses for
// the "task" resource type.
const TASK_DOMAIN_BADGE_CLASS = "bg-indigo-50 text-indigo-700";

export function taskDomainBadgeClass() {
  return TASK_DOMAIN_BADGE_CLASS;
}

// À faire -> En cours -> Terminée -> À faire, used for the quick row checkbox cycle.
const STATUS_CYCLE = { a_faire: "en_cours", en_cours: "termine", termine: "a_faire" };

export function nextTaskStatus(status) {
  return STATUS_CYCLE[status] || "a_faire";
}

// --- Operational views + filters: pure, unit-tested functions -------------
//
// due_date is always a bare "YYYY-MM-DD" string. `new Date("YYYY-MM-DD")`
// parses it as UTC midnight, which shifts to the previous local calendar day
// in any timezone behind UTC. Parsing the components by hand into a local
// Date (new Date(y, m-1, d)) avoids that. Also used by lib/timeline.js.
export function parseLocalDate(dueDate) {
  if (!dueDate) return null;
  const parts = dueDate.split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d);
}

// "16/10/2026" for a bare due date, read as a local day (never through new Date("YYYY-MM-DD"), which shifts it in UTC-minus zones).
export function formatDueDay(dueDate) {
  return parseLocalDate(dueDate)?.toLocaleDateString("fr-FR") || "";
}

export function startOfLocalDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

// Monday-based calendar week (ISO 8601): Monday 00:00 local time (inclusive)
// to the following Monday 00:00 (exclusive).
function currentWeekBounds(now) {
  const today = startOfLocalDay(now);
  const daysSinceMonday = (today.getDay() + 6) % 7; // Sun=0..Sat=6 -> Mon=0..Sun=6
  const start = new Date(today);
  start.setDate(today.getDate() - daysSinceMonday);
  const end = new Date(start);
  end.setDate(start.getDate() + 7);
  return { start, end };
}

export function isTaskDueToday(task, now = new Date()) {
  const due = parseLocalDate(task.due_date);
  if (!due) return false;
  return due.getTime() === startOfLocalDay(now).getTime();
}

// "Cette semaine": non-done, due_date within the current Monday-Sunday
// window. An overdue task (due last week or earlier) is never "this week",
// and isTaskOverdue stays the one definition of "late".
export function isTaskThisWeek(task, now = new Date()) {
  if (task.done) return false;
  const due = parseLocalDate(task.due_date);
  if (!due) return false;
  const { start, end } = currentWeekBounds(now);
  return due >= start && due < end;
}

export function isTaskCritical(task) {
  return !task.done && task.priority === "critical";
}

export function isMyOpenTask(task, userId) {
  return !task.done && (task.assigned_to || []).includes(userId);
}

// Backs the simple "Échéance" filter; reuses isTaskOverdue and
// isTaskThisWeek rather than a third date definition.
export const DUE_DATE_FILTERS = [
  { id: "all", label: "Toutes" },
  { id: "today", label: "Aujourd'hui" },
  { id: "this_week", label: "Cette semaine" },
  { id: "overdue", label: "En retard" },
  { id: "no_date", label: "Sans échéance" },
];

export function matchesDueDateFilter(task, filterId, now = new Date()) {
  switch (filterId) {
    case "today":
      return isTaskDueToday(task, now);
    case "this_week":
      return isTaskThisWeek(task, now);
    case "overdue":
      return isTaskOverdue(task);
    case "no_date":
      return !task.due_date;
    default:
      return true;
  }
}

// Local, client-side search: title + description, case-insensitive.
export function taskMatchesSearch(task, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  return (task.title || "").toLowerCase().includes(q) || (task.description || "").toLowerCase().includes(q);
}

// Single combined predicate for every Tasks.jsx filter dimension (statut,
// type, responsable, prospect, domaine, priorité, échéance, recherche), so
// the combination is tested in one place. `currentUserId` and `now` are
// explicit params, keeping this a pure, directly-testable function.
export function taskMatchesFilters(task, filters, currentUserId) {
  const {
    statusFilter = "all",
    typeFilter = "all",
    responsableFilter = "all",
    prospectId = null,
    domainFilter = "all",
    priorityFilter = "all",
    dueDateFilter = "all",
    search = "",
    now = new Date(),
  } = filters || {};
  const assignees = task.assigned_to || [];

  if (!taskMatchesStatusFilter(task, statusFilter)) return false;
  if (typeFilter !== "all" && (task.task_type || "autre") !== typeFilter) return false;
  if (responsableFilter === "moi" && !assignees.includes(currentUserId)) return false;
  if (responsableFilter !== "all" && responsableFilter !== "moi" && !assignees.includes(responsableFilter)) return false;
  if (prospectId && task.prospect_id !== prospectId) return false;
  if (domainFilter !== "all" && (task.domain || null) !== domainFilter) return false;
  if (priorityFilter !== "all" && (task.priority || "medium") !== priorityFilter) return false;
  if (dueDateFilter !== "all" && !matchesDueDateFilter(task, dueDateFilter, now)) return false;
  if (!taskMatchesSearch(task, search)) return false;
  return true;
}

// Trail of status changes kept by the backend (`status_history`, oldest first). An older task has none: the timeline then shows its
// creation only, never an invented change.
export function statusTimeline(task) {
  const changes = Array.isArray(task?.status_history) ? task.status_history : [];
  return [...changes].sort((a, b) => new Date(a.at) - new Date(b.at));
}

// The latest time the task was put into `status` (null if it never was, or if the task predates the trail).
export function lastEntryInto(task, status) {
  const entries = statusTimeline(task).filter((e) => e.status === status);
  return entries.length ? entries[entries.length - 1] : null;
}

// « 3 j », « 5 h », « 12 min »: how long ago `iso` was, in the largest whole unit (never negative).
export function elapsedSince(iso, now = new Date()) {
  const ms = Math.max(0, now - new Date(iso));
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) return `${Math.max(1, minutes)} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  return `${Math.floor(hours / 24)} j`;
}
